import type {
  VariantManagementColumn,
  VariantManagementIdentifier,
  VariantManagementRow,
  VariantMappingRow,
} from '../types/variantMapping.ts';

export const VARIANT_MANAGEMENT_PAGE_SIZES = [25, 50, 100, 250] as const;
export const VARIANT_MANAGEMENT_DEFAULT_PAGE_SIZE = 50;

export function formatIdentifierTypeHeader(type: string): string {
  return type.trim().toUpperCase();
}

export function resolveVariantManagementPageSize(raw: unknown): number {
  const numeric = typeof raw === 'number' ? raw : Number(raw);
  if (VARIANT_MANAGEMENT_PAGE_SIZES.includes(numeric as (typeof VARIANT_MANAGEMENT_PAGE_SIZES)[number])) {
    return numeric;
  }
  return VARIANT_MANAGEMENT_DEFAULT_PAGE_SIZE;
}

type VariantManagementCatalogEntry = {
  master_product_id: string;
  display_name?: string;
  code?: string;
  aliases?: readonly string[];
  eve_id?: number;
  product_description?: string | null;
  linked_identifier_count?: number;
  linked_identifiers?: readonly { type: string; value: string; linkId?: string }[];
};

function catalogSearchFields(entry: VariantManagementCatalogEntry): string[] {
  return [
    entry.display_name ?? '',
    entry.code ?? '',
    ...(entry.aliases ?? []),
    entry.eve_id != null ? `EVE-${entry.eve_id}` : '',
    entry.product_description ?? '',
  ];
}

function catalogEveId(entry: VariantManagementCatalogEntry): number {
  return typeof entry.eve_id === 'number' && Number.isFinite(entry.eve_id) ? entry.eve_id : 0;
}

function catalogLinkedCount(entry: VariantManagementCatalogEntry): number {
  if (typeof entry.linked_identifier_count === 'number' && Number.isFinite(entry.linked_identifier_count)) {
    return Math.max(0, entry.linked_identifier_count);
  }
  return 0;
}

/** An EVE row is shown only when an identifier is mapped to that master product. */
function entryHasMappedIdentifier(
  entry: VariantManagementCatalogEntry,
  matchedObservationCount: number,
): boolean {
  if (catalogLinkedCount(entry) > 0) return true;
  const linked = entry.linked_identifiers ?? [];
  if (linked.some((link) => link.type.trim() !== '' && link.value.trim() !== '')) return true;
  return matchedObservationCount > 0;
}

function appendIdentifier(
  list: VariantManagementIdentifier[],
  type: string | null | undefined,
  value: string | null | undefined,
  linkId?: string | null,
): void {
  const trimmedType = type?.trim().toLowerCase() ?? '';
  const trimmedValue = value?.trim() ?? '';
  if (!trimmedType || !trimmedValue) return;
  const existing = list.find((item) => item.type === trimmedType && item.value === trimmedValue);
  if (existing) {
    if (!existing.linkId && linkId) existing.linkId = linkId;
    return;
  }
  list.push({
    type: trimmedType,
    value: trimmedValue,
    ...(linkId ? { linkId } : {}),
  });
}

function catalogProductDescription(entry: VariantManagementCatalogEntry): string | null {
  const raw = entry.product_description?.trim();
  return raw ? raw : null;
}

function observationSearchFields(row: VariantMappingRow): string[] {
  return [
    row.externalIdentifier,
    row.productName,
    row.identifierType,
    row.sourceChannel,
    row.ingestionTransport,
    row.matchedVariant?.display_name ?? '',
  ];
}

export function buildVariantManagementRows(
  catalog: ReadonlyArray<VariantManagementCatalogEntry>,
  mappingRows: readonly VariantMappingRow[],
  options?: { identifiersUnavailable?: boolean },
): VariantManagementRow[] {
  const observationsByVariant = new Map<string, VariantMappingRow[]>();
  for (const row of mappingRows) {
    const variantId = row.matchedVariant?.master_product_id;
    if (!variantId) continue;
    const list = observationsByVariant.get(variantId) ?? [];
    list.push(row);
    observationsByVariant.set(variantId, list);
  }

  const visibleCatalog = catalog.filter((entry) =>
    entryHasMappedIdentifier(entry, observationsByVariant.get(entry.master_product_id)?.length ?? 0),
  );

  const identifiersByVariant = new Map<string, VariantManagementIdentifier[]>();
  for (const entry of visibleCatalog) {
    identifiersByVariant.set(entry.master_product_id, []);
  }

  if (options?.identifiersUnavailable) {
    return visibleCatalog.map((entry) => ({
      variantId: entry.master_product_id,
      identifiers: [],
      eveId: catalogEveId(entry),
      linkedIdentifierCount: 0,
      productDescription: catalogProductDescription(entry),
      searchFields: catalogSearchFields(entry),
    }));
  }

  for (const [variantId, matched] of observationsByVariant) {
    if (!identifiersByVariant.has(variantId)) continue;
    for (const row of matched) {
      appendIdentifier(identifiersByVariant.get(variantId)!, row.identifierType, row.externalIdentifier);
    }
  }

  for (const entry of visibleCatalog) {
    const list = identifiersByVariant.get(entry.master_product_id);
    if (!list) continue;
    for (const link of entry.linked_identifiers ?? []) {
      appendIdentifier(list, link.type, link.value, link.linkId);
    }
  }

  return visibleCatalog.map((entry) => ({
    variantId: entry.master_product_id,
    identifiers: identifiersByVariant.get(entry.master_product_id) ?? [],
    eveId: catalogEveId(entry),
    linkedIdentifierCount: catalogLinkedCount(entry),
    productDescription: catalogProductDescription(entry),
    searchFields: [
      ...catalogSearchFields(entry),
      ...(observationsByVariant.get(entry.master_product_id) ?? []).flatMap(observationSearchFields),
    ],
  }));
}

export function buildVariantManagementColumns(rows: readonly VariantManagementRow[]): VariantManagementColumn[] {
  const maxByType = new Map<string, number>();
  for (const row of rows) {
    const counts = new Map<string, number>();
    for (const identifier of row.identifiers) {
      const type = identifier.type.trim();
      if (!type) continue;
      counts.set(type, (counts.get(type) ?? 0) + 1);
    }
    for (const [type, count] of counts) {
      maxByType.set(type, Math.max(maxByType.get(type) ?? 0, count));
    }
  }

  return Array.from(maxByType.entries())
    .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .flatMap(([type, count]) =>
      Array.from({ length: count }, (_, index) => ({
        key: `${type}:${index}`,
        typeName: formatIdentifierTypeHeader(type),
      })),
    );
}

export function variantManagementCellValue(
  row: VariantManagementRow,
  column: VariantManagementColumn,
): string {
  return variantManagementCellIdentifier(row, column)?.value ?? '';
}

export function variantManagementCellIdentifier(
  row: VariantManagementRow,
  column: VariantManagementColumn,
): VariantManagementIdentifier | undefined {
  const [type, indexRaw] = column.key.split(':');
  const index = Number(indexRaw);
  const values = row.identifiers.filter((identifier) => identifier.type === type);
  return values[index];
}

function managementRowSearchHaystack(row: VariantManagementRow): string[] {
  return [
    ...(row.searchFields ?? []),
    ...row.identifiers.flatMap((identifier) => [identifier.value, identifier.type]),
  ];
}

export function filterVariantManagementRows(
  rows: readonly VariantManagementRow[],
  query: string,
): VariantManagementRow[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return [...rows];
  return rows.filter((row) =>
    managementRowSearchHaystack(row).some((field) => field.toLowerCase().includes(trimmed)),
  );
}
